<template>
  <article :id="operationId" class="scroll-mt-8 border-t border-zinc-200 py-10 first:border-t-0">
    <div class="flex flex-wrap items-center gap-3">
      <span class="rounded-md bg-zinc-900 px-2.5 py-1 text-xs font-semibold uppercase text-white">{{ method }}</span>
      <code class="text-sm text-zinc-600">{{ path }}</code>
      <button type="button" class="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-zinc-400 transition hover:bg-zinc-100 hover:text-zinc-900" @click="copyEndpoint">
        <PhCheck v-if="endpointCopied" class="h-3.5 w-3.5 text-emerald-600" weight="bold" />
        <PhCopy v-else class="h-3.5 w-3.5" weight="duotone" />
        {{ endpointCopied ? "Copiado" : "Copiar endpoint" }}
      </button>
      <button type="button" class="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-zinc-400 transition hover:bg-zinc-100 hover:text-zinc-900" @click="openRequestTester">
        <PhPlay class="h-3.5 w-3.5" weight="duotone" /> Testar requisição
      </button>
    </div>
    <div v-if="requestTesterOpen" class="fixed inset-0 z-50 flex items-center justify-center bg-emerald-950/30 px-4 backdrop-blur-sm" @click.self="requestTesterOpen = false">
      <section class="max-h-[90vh] w-full max-w-[1096px] overflow-y-auto rounded-xl border border-zinc-200 bg-white p-6 shadow-xl" role="dialog" aria-modal="true" aria-labelledby="request-tester-title">
        <div class="flex items-start justify-between gap-4">
          <div>
            <h3 id="request-tester-title" class="text-lg font-semibold text-zinc-950">Testar requisição</h3>
            <p class="mt-1 break-all font-mono text-xs text-zinc-500">{{ props.method.toUpperCase() }} {{ testRequestUrl }}</p>
          </div>
          <button type="button" class="text-sm text-zinc-400 hover:text-zinc-950" @click="requestTesterOpen = false">Fechar</button>
        </div>
        <div class="mt-6 grid gap-8 lg:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)]">
          <div>
            <p class="text-xs font-semibold uppercase tracking-wide text-zinc-400">Dados da requisição</p>
            <label v-if="props.authHeader" class="mt-4 block text-sm font-medium text-zinc-700">{{ props.authHeader }}
          <input v-model="testToken" type="password" placeholder="Seu token" class="mt-1.5 block w-full rounded-md border border-zinc-300 px-3 py-2.5 text-sm outline-none focus:border-zinc-500 focus:ring-2 focus:ring-zinc-200" />
            </label>
            <div v-if="pathParameters.length" class="mt-4 grid gap-4">
          <label v-for="parameter in pathParameters" :key="parameter.name" class="block text-sm font-medium text-zinc-700">{{ parameter.name }}
            <input v-model="testParameters[parameter.name || '']" type="text" :placeholder="`Valor de ${parameter.name}`" class="mt-1.5 block w-full rounded-md border border-zinc-300 px-3 py-2.5 text-sm outline-none focus:border-zinc-500 focus:ring-2 focus:ring-zinc-200" />
          </label>
            </div>
            <div v-if="queryParameters.length" class="mt-4 grid gap-4">
          <label v-for="parameter in queryParameters" :key="parameter.name" class="block text-sm font-medium text-zinc-700">{{ parameter.name }} <span class="font-normal text-zinc-400">(query)</span>
            <input v-model="testParameters[parameter.name || '']" type="text" :placeholder="parameter.description || `Valor de ${parameter.name}`" class="mt-1.5 block w-full rounded-md border border-zinc-300 bg-white px-3 py-2.5 text-sm outline-none focus:border-zinc-500 focus:ring-2 focus:ring-zinc-200" />
          </label>
            </div>
            <label v-if="requestBody" class="mt-4 block text-sm font-medium text-zinc-700">Corpo JSON
          <textarea v-model="testBody" rows="8" class="mt-1.5 block w-full rounded-md border border-zinc-300 px-3 py-2.5 font-mono text-xs outline-none focus:border-zinc-500 focus:ring-2 focus:ring-zinc-200"></textarea>
            </label>
          </div>
          <div class="min-w-0 border-t border-zinc-200 pt-6 lg:border-l lg:border-t-0 lg:pl-8 lg:pt-0">
            <p class="text-xs font-semibold uppercase tracking-wide text-zinc-400">Resultado</p>
            <div v-if="testError || testResponse" class="mt-4">
              <p v-if="testError" class="rounded-md bg-red-50 p-3 text-sm leading-6 text-red-700">{{ testError }}</p>
              <div v-else>
                <p class="text-sm font-semibold text-zinc-950">Resposta <span :class="testStatus && testStatus < 400 ? 'text-emerald-600' : 'text-red-600'">{{ testStatus }}</span></p>
                <pre class="mt-3 max-h-[28rem] overflow-auto rounded-lg bg-zinc-950 p-4 text-xs leading-5 text-zinc-200"><code>{{ testResponse }}</code></pre>
              </div>
            </div>
            <div v-else class="mt-4 flex min-h-48 items-center justify-center rounded-lg border border-dashed border-zinc-200 bg-zinc-50 px-6 text-center text-sm leading-6 text-zinc-500">
              A resposta da API aparecerá aqui depois do envio.
            </div>
          </div>
        </div>
        <div class="mt-8 flex justify-end pt-1">
          <button type="button" :disabled="testLoading" class="inline-flex items-center gap-2 rounded-md bg-zinc-950 px-4 py-2.5 text-sm font-medium text-white hover:bg-zinc-800 disabled:cursor-wait disabled:opacity-60" @click="runRequest">
            <PhSpinner v-if="testLoading" class="h-4 w-4 animate-spin" />
            <PhPaperPlaneTilt v-else class="h-4 w-4" weight="duotone" />
            {{ testLoading ? "Enviando..." : "Enviar requisição" }}
          </button>
        </div>
      </section>
    </div>
    <div class="relative mt-4">
      <button
        type="button"
        class="relative mb-2 inline-flex h-9 w-9 items-center justify-center rounded-md border border-zinc-200 text-zinc-400 transition hover:border-zinc-400 hover:text-zinc-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-zinc-300 lg:absolute lg:-left-12 lg:top-1/2 lg:mb-0 lg:-translate-y-1/2"
        :aria-label="linkCopied ? 'Link do endpoint copiado' : 'Copiar link do endpoint'"
        :title="linkCopied ? 'Link copiado' : 'Copiar link do endpoint'"
        @click="copyLink"
      >
        <PhCheck v-if="linkCopied" class="h-4 w-4 text-emerald-600" weight="bold" aria-hidden="true" />
        <PhCopy v-else class="h-4 w-4" weight="duotone" aria-hidden="true" />
      </button>
      <h3 class="flex flex-wrap items-center gap-3 text-xl font-semibold text-zinc-950">
        <span>{{ operation.summary || "Operação" }}</span>
        <span v-if="operation['x-badge']" class="rounded-full bg-amber-50 px-2 py-1 text-[10px] font-semibold uppercase tracking-wide text-amber-700">{{ operation['x-badge'] }}</span>
      </h3>
    </div>
    <div v-if="operation.description" class="prose prose-zinc mt-2 max-w-none text-sm text-zinc-600" v-html="markdownToHtml(operation.description)"></div>

    <section class="mt-6">
      <div class="flex items-center justify-between gap-3">
        <h4 class="text-sm font-semibold text-zinc-950">Requisição</h4>
        <div class="flex items-center gap-1 rounded-md bg-zinc-100 p-1">
          <button v-for="language in requestLanguages" :key="language.key" type="button" :class="activeLanguage === language.key ? 'bg-white text-zinc-950 shadow-sm' : 'text-zinc-500 hover:text-zinc-950'" class="rounded px-2 py-1 text-xs font-medium transition" @click="activeLanguage = language.key">
            {{ language.label }}
          </button>
        </div>
      </div>
      <div class="relative mt-3">
        <pre class="overflow-x-auto rounded-lg bg-zinc-950 p-4 pr-12 text-xs leading-5 text-zinc-200"><code>{{ activeExample }}</code></pre>
        <button
          type="button"
          class="absolute right-2 top-2 rounded-md p-2 text-zinc-400 transition hover:bg-zinc-800 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-zinc-400"
          :aria-label="copied ? 'Copiado' : `Copiar ${activeLanguage}`"
          @click="copyActiveRequest"
        >
          <svg v-if="!copied" class="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
            <rect width="13" height="13" x="9" y="9" rx="2" />
            <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
          </svg>
          <svg v-else class="h-4 w-4 text-emerald-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
            <path d="m5 12 4 4L19 6" />
          </svg>
        </button>
      </div>
    </section>

    <section v-if="operation.parameters?.length" class="mt-6">
      <h4 class="text-sm font-semibold text-zinc-950">Parâmetros</h4>
      <div class="mt-3 overflow-hidden rounded-lg border border-zinc-200">
        <div v-for="parameter in operation.parameters" :key="String(parameter.name)" class="border-b border-zinc-200 p-4 last:border-b-0">
          <code class="text-sm font-medium text-zinc-900">{{ parameter.name }}</code>
          <span class="ml-2 text-xs text-zinc-500">{{ parameter.in }}</span>
          <span v-if="parameter.required" class="ml-2 rounded-full bg-red-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-red-600">Obrigatório</span>
          <div v-if="parameter.description" class="prose prose-zinc mt-1 text-sm text-zinc-600" v-html="markdownToHtml(String(parameter.description))"></div>
        </div>
      </div>
    </section>

    <section v-if="operation.requestBody" class="mt-6">
      <h4 class="text-sm font-semibold text-zinc-950">Corpo da requisição</h4>
      <div class="relative mt-3">
        <pre class="overflow-x-auto rounded-lg bg-zinc-950 p-4 pr-12 text-xs leading-5 text-zinc-200"><code>{{ requestBodyFormatted }}</code></pre>
        <button
          type="button"
          class="absolute right-2 top-2 rounded-md p-2 text-zinc-400 transition hover:bg-zinc-800 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-zinc-400"
          :aria-label="bodyCopied ? 'Copiado' : 'Copiar corpo da requisição'"
          @click="copyBody"
        >
          <svg v-if="!bodyCopied" class="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
            <rect width="13" height="13" x="9" y="9" rx="2" />
            <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
          </svg>
          <svg v-else class="h-4 w-4 text-emerald-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
            <path d="m5 12 4 4L19 6" />
          </svg>
        </button>
      </div>
    </section>

    <section v-if="operation.responses" class="mt-6">
      <h4 class="text-sm font-semibold text-zinc-950">Respostas</h4>
      <div class="mt-3 space-y-2">
        <div v-for="(response, status) in operation.responses" :key="status" class="rounded-lg border border-zinc-200 p-4">
          <span class="font-mono text-sm font-semibold text-zinc-900">{{ status }}</span>
          <span class="ml-3 text-sm text-zinc-600">{{ response.description }}</span>
          <pre v-if="responseExample(response)" class="mt-3 overflow-x-auto rounded-md bg-zinc-950 p-3 text-xs leading-5 text-zinc-200"><code>{{ responseExample(response) }}</code></pre>
        </div>
      </div>
    </section>
  </article>
</template>

<script setup lang="ts">
import type { ApiOperation as Operation } from "./apiTypes";
import { markdownToHtml } from "../../lib/markdown";
import { computed, ref } from "vue";
import { PhCheck, PhCopy, PhPaperPlaneTilt, PhPlay, PhSpinner } from "@phosphor-icons/vue";

const props = defineProps<{ method: string; path: string; operation: Operation; baseUrl?: string; authHeader?: string; schemas?: Record<string, Record<string, any>> }>();
const operationId = `${props.method}-${props.path}`.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
const requestContent = props.operation.requestBody?.content?.["application/json"];
const requestExample = requestContent?.example ?? exampleFromSchema(requestContent?.schema, props.schemas);
const requestBody = requestExample ? JSON.stringify(requestExample) : undefined;
const requestBodyFormatted = requestExample ? JSON.stringify(requestExample, null, 2) : "{}";
const requestPath = props.path.replace(/\{([^}]+)\}/g, "<$1>");
const curlExample = [
  `curl -X ${props.method.toUpperCase()} '${props.baseUrl || "https://api.example.com"}${requestPath}'`,
  props.authHeader ? `  -H '${props.authHeader}: <seu-token>'` : "",
  requestBody ? `  -H 'Content-Type: application/json'\\\n  -d '${requestBody.replace(/'/g, "\\\\'")}'` : "",
].filter(Boolean).join(" \\\n");
const requestUrl = `${props.baseUrl || "https://api.example.com"}${requestPath}`;
const requestLanguages = [
  { key: "curl", label: "cURL" },
  { key: "javascript", label: "JavaScript" },
  { key: "php", label: "PHP" },
] as const;
const activeLanguage = ref<(typeof requestLanguages)[number]["key"]>("curl");
const javascriptExample = [
  `const response = await fetch("${requestUrl}", {`,
  `  method: "${props.method.toUpperCase()}",`,
  "  headers: {",
  props.authHeader ? `    "${props.authHeader}": "<seu-token>",` : '    "Content-Type": "application/json",',
  requestBody ? "    \"Content-Type\": \"application/json\"," : "",
  "  },",
  requestBody ? `  body: ${requestBody},` : "",
  "});",
  "",
  "const data = await response.json();",
].filter(Boolean).join("\n");
const phpExample = [
  `$ch = curl_init("${requestUrl}");`,
  "curl_setopt_array($ch, [",
  "    CURLOPT_RETURNTRANSFER => true,",
  `    CURLOPT_CUSTOMREQUEST => "${props.method.toUpperCase()}",`,
  "    CURLOPT_HTTPHEADER => [",
  props.authHeader ? `        "${props.authHeader}: <seu-token>",` : '        "Content-Type: application/json",',
  requestBody ? "        \"Content-Type: application/json\"," : "",
  "    ],",
  requestBody ? `    CURLOPT_POSTFIELDS => '${requestBody.replace(/'/g, "\\'")}',` : "",
  "]);",
  "$response = curl_exec($ch);",
  "curl_close($ch);",
].filter(Boolean).join("\n");
const activeExample = computed(() => ({ curl: curlExample, javascript: javascriptExample, php: phpExample }[activeLanguage.value]));
const responseExample = (response: Record<string, any>) => {
  const content = response.content?.["application/json"];
  if (!content) return "";
  if (content.example !== undefined) return JSON.stringify(content.example, null, 2);
  if (content.examples) {
    const example = Object.values(content.examples)[0] as { value?: unknown };
    if (example?.value !== undefined) return JSON.stringify(example.value, null, 2);
  }
  const example = exampleFromSchema(content.schema, props.schemas);
  return example === undefined ? "Resposta sem corpo documentado" : JSON.stringify(example, null, 2);
};
const copied = ref(false);
const endpointCopied = ref(false);
const requestTesterOpen = ref(false);
const testToken = ref("");
const testBody = ref(requestBody || "");
const testParameters = ref<Record<string, string>>({});
const testLoading = ref(false);
const testStatus = ref<number | null>(null);
const testResponse = ref("");
const testError = ref("");
const operationParameters = (props.operation.parameters || []) as Array<{ name?: string; in?: string; description?: string }>;
const pathParameters = operationParameters.filter((parameter) => parameter.in === "path" && parameter.name);
const queryParameters = operationParameters.filter((parameter) => parameter.in === "query" && parameter.name);
const testRequestUrl = computed(() => {
  let url = requestUrl;
  pathParameters.forEach((parameter) => { url = url.replace(`<${parameter.name}>`, encodeURIComponent(testParameters.value[parameter.name || ""] || `<${parameter.name}>`)); });
  const query = queryParameters.filter((parameter) => testParameters.value[parameter.name || ""]).map((parameter) => `${encodeURIComponent(parameter.name || "")}=${encodeURIComponent(testParameters.value[parameter.name || ""])}`);
  return query.length ? `${url}?${query.join("&")}` : url;
});
let copyTimeout: ReturnType<typeof setTimeout> | undefined;
const bodyCopied = ref(false);
const linkCopied = ref(false);
let bodyCopyTimeout: ReturnType<typeof setTimeout> | undefined;
let linkCopyTimeout: ReturnType<typeof setTimeout> | undefined;
const copyLink = async () => {
  await navigator.clipboard.writeText(`${window.location.origin}${window.location.pathname}#${operationId}`);
  linkCopied.value = true;
  clearTimeout(linkCopyTimeout);
  linkCopyTimeout = setTimeout(() => { linkCopied.value = false; }, 2000);
};
const copyActiveRequest = async () => {
  await navigator.clipboard.writeText(activeExample.value);
  copied.value = true;
  clearTimeout(copyTimeout);
  copyTimeout = setTimeout(() => { copied.value = false; }, 2000);
};
const copyEndpoint = async () => {
  await navigator.clipboard.writeText(`${props.baseUrl || ""}${requestPath}`);
  endpointCopied.value = true;
  window.setTimeout(() => { endpointCopied.value = false; }, 2000);
};
const openRequestTester = () => {
  testToken.value = "";
  testBody.value = requestBody || "";
  testParameters.value = {};
  testStatus.value = null;
  testResponse.value = "";
  testError.value = "";
  requestTesterOpen.value = true;
};
const runRequest = async () => {
  testLoading.value = true;
  testError.value = "";
  testResponse.value = "";
  try {
    const headers: Record<string, string> = {};
    if (props.authHeader) headers[props.authHeader] = testToken.value;
    if (testBody.value) headers["Content-Type"] = "application/json";
    const response = await fetch("/api/request-proxy", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ url: testRequestUrl.value, method: props.method, headers, body: testBody.value || undefined }) });
    const proxyResult = await response.json();
    if (!response.ok) throw new Error(proxyResult.error || "Não foi possível executar a requisição.");
    testStatus.value = proxyResult.status;
    const text = proxyResult.body;
    if (proxyResult.content_type?.includes("text/html") || /^\s*<!doctype html/i.test(text)) {
      throw new Error(`A URL respondeu uma página HTML (${proxyResult.status}). Confira a Base URL da API e o prefixo do servidor definido na documentação.`);
    }
    try { testResponse.value = JSON.stringify(JSON.parse(text), null, 2); } catch { testResponse.value = text; }
  } catch (error) {
    testError.value = error instanceof Error ? error.message : "Não foi possível executar a requisição.";
  } finally {
    testLoading.value = false;
  }
};
const copyBody = async () => {
  const body = requestBodyFormatted;
  await navigator.clipboard.writeText(body);
  bodyCopied.value = true;
  clearTimeout(bodyCopyTimeout);
  bodyCopyTimeout = setTimeout(() => { bodyCopied.value = false; }, 2000);
};

function exampleFromSchema(schema: Record<string, any> | undefined, schemas?: Record<string, Record<string, any>>): unknown {
  if (!schema) return undefined;
  if (schema.$ref) return exampleFromSchema(schemas?.[schema.$ref.split("/").pop() || ""], schemas);
  if (schema.example !== undefined) return schema.example;
  if (schema.enum?.length) return schema.enum[0];
  if (schema.type === "array") return [exampleFromSchema(schema.items, schemas)];
  if (schema.type === "object" || schema.properties) {
    return Object.fromEntries(Object.entries(schema.properties || {}).map(([key, value]) => [key, exampleFromSchema(value as Record<string, any>, schemas)]));
  }
  if (schema.type === "integer" || schema.type === "number") return 0;
  if (schema.type === "boolean") return true;
  if (schema.format === "date-time") return "2026-01-01T12:00:00Z";
  if (schema.format === "uuid") return "00000000-0000-0000-0000-000000000000";
  return "string";
}
</script>

<script lang="ts">
export default { name: "ApiOperation" };
</script>
